/**
 * entityLinks — the single source of truth for deep links to a specific record.
 *
 * Every dashboard card, table row, notification, and calendar action builds its
 * navigation target here so no component hand-assembles `/exclusive-dashboard?open=…`
 * style paths. Modal-hosted records (bookings, owner payouts) use an `?open=<id>`
 * query param the host page reads on mount; routed records (leads, clients) use
 * their existing `/:id` route; calendar tasks use `/calendar?focus=<task_id>`.
 *
 * Booking-type spellings differ across the app — the dashboard/notifications emit
 * `'exclusive'` / `'non-exclusive'` (hyphen) while agent_tasks use `'non_exclusive'`
 * (underscore). normalizeBookingType() collapses both.
 */

import { getOperationalBookingReference } from './bookingReference';

/**
 * Collapse the booking_type variants seen across the codebase to a canonical
 * 'exclusive' | 'non_exclusive'. Returns null when unrecognized.
 */
export function normalizeBookingType(type) {
  if (!type) return null;
  const t = String(type).toLowerCase().replace(/-/g, '_');
  if (t === 'exclusive') return 'exclusive';
  if (t === 'non_exclusive' || t === 'nonexclusive') return 'non_exclusive';
  return null;
}

/** Pull a numeric-ish booking id out of the many shapes callers pass. */
function resolveBookingId(source) {
  if (source === null || source === undefined) return null;
  if (typeof source === 'number' || typeof source === 'string') return source;
  return (
    source.id ??
    source.booking_id ??
    source.exclusive_booking_id ??
    source.non_exclusive_booking_id ??
    source.booking?.id ??
    null
  );
}

/** Infer the canonical booking type, preferring an explicit type then the FK shape. */
function resolveBookingType(source) {
  if (!source || typeof source !== 'object') return null;
  const explicit = normalizeBookingType(source.type || source.booking_type);
  if (explicit) return explicit;
  if (source.exclusive_booking_id != null) return 'exclusive';
  if (source.non_exclusive_booking_id != null) return 'non_exclusive';
  return null;
}

export const BOOKING_HOST_PATHS = {
  exclusive: '/exclusive-dashboard',
  non_exclusive: '/non-exclusive-bookings-dashboard',
};

/** The query param a booking host page reads to auto-open one record. */
export const BOOKING_OPEN_PARAM = 'open';
/** Legacy alias still emitted on in-flight notification links; ExclusiveDashboard also honors it. */
export const BOOKING_OPEN_PARAM_LEGACY = 'bookingId';
/** The query param a booking host page reads to pre-apply an owner filter. */
export const OWNER_FILTER_PARAM = 'owner';
/** The query param the calendar reads to focus + open one task. */
export const TASK_FOCUS_PARAM = 'focus';

/**
 * Deep link to ONE booking's modal on its host dashboard.
 * Accepts a booking-like object ({ id, type/booking_type, … } or an FK shape like
 * { exclusive_booking_id }) or an explicit ({ id, type }). Returns null if the
 * booking type can't be resolved (caller then leaves the element non-navigating).
 */
export function linkToBooking(source) {
  const id = resolveBookingId(source);
  const type = resolveBookingType(source);
  if (id == null || !type) return null;
  const base = BOOKING_HOST_PATHS[type];
  return `${base}?${BOOKING_OPEN_PARAM}=${encodeURIComponent(id)}`;
}

/** Routed lead detail page. */
export function linkToLead(leadId) {
  if (leadId == null || leadId === '') return null;
  return `/leads/${encodeURIComponent(leadId)}`;
}

/** Routed client detail page. */
export function linkToClient(clientId) {
  if (clientId == null || clientId === '') return null;
  return `/clients/${encodeURIComponent(clientId)}`;
}

/** Calendar, focused on and opening ONE agent_task. */
export function linkToTask(taskId) {
  if (taskId == null || taskId === '') return null;
  return `/calendar?${TASK_FOCUS_PARAM}=${encodeURIComponent(taskId)}`;
}

/**
 * Owner-payout dashboard, opening ONE payout row.
 * Accepts an owner_payout id or a row/notification carrying owner_payout_id / id.
 */
export function linkToPayout(source) {
  let id = null;
  if (source === null || source === undefined) return null;
  if (typeof source === 'number' || typeof source === 'string') {
    id = source;
  } else {
    id = source.owner_payout_id ?? source.id ?? null;
  }
  if (id == null || id === '') return null;
  return `/owner-payouts?${BOOKING_OPEN_PARAM}=${encodeURIComponent(id)}`;
}

/**
 * Owner-payout dashboard, pre-filtered to ONE owner (the dashboard's per-owner
 * aggregate row is not a single payout, so it filters rather than opens).
 */
export function linkToOwnerPayoutsByOwner(ownerId) {
  if (ownerId == null || ownerId === '') return null;
  return `/owner-payouts?${OWNER_FILTER_PARAM}=${encodeURIComponent(ownerId)}`;
}

/** Human reference for a booking (e.g. "QB-0064"), for toasts/labels. */
export function bookingReferenceLabel(source) {
  const ref = getOperationalBookingReference(source);
  if (ref) return ref;
  const id = resolveBookingId(source);
  return id != null ? `#${id}` : 'booking';
}
